// ** React Imports

import Badge from '@mui/material/Badge'
// ** MUI Imports
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import MuiMenu, { MenuProps } from '@mui/material/Menu'
import { styled, Theme, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ReactNode, SyntheticEvent, useEffect, useState } from 'react'
// ** Third Party Components
import PerfectScrollbarComponent from 'react-perfect-scrollbar'
import { useLocation, useNavigate } from 'react-router-dom'
// ** Icon Imports
import Icon from 'src/@core/components/icon'
import { Settings } from 'src/@core/context/settingsContext'
// ** Type Imports
import { ThemeColor } from 'src/@core/layouts/types'
import { Notification } from '@/components/organisms/notification'
import { useUser } from '@/hooks/useUser'
import { WmsNotificationBell } from '@/modules/wms/clusters/general/components/WmsNotificationBell'
import { queryClient } from '@/utils/queryClient'
import { useReadAllNotification, useReadNotifications } from '../../services/actionUpdateNotification.service'
import { useGetNotification } from '../../services/fetchNotification.services'

export type NotificationsType = {
  meta: string
  title: string
  subtitle: string
  url: string
} & (
  | { avatarAlt: string; avatarImg: string; avatarText?: never; avatarColor?: never; avatarIcon?: never }
  | {
      avatarAlt?: never
      avatarImg?: never
      avatarText: string
      avatarIcon?: never
      avatarColor?: ThemeColor
    }
  | {
      avatarAlt?: never
      avatarImg?: never
      avatarText?: never
      avatarIcon: ReactNode
      avatarColor?: ThemeColor
    }
)
interface Props {
  settings: Settings
}

// ** Styled Menu component
const Menu = styled(MuiMenu)<MenuProps>(({ theme }) => ({
  '& .MuiMenu-paper': {
    maxWidth: 'min-content',
    overflow: 'hidden',
    marginTop: theme.spacing(4.5)
  }
}))

// ** Styled PerfectScrollbar component
const PerfectScrollbar = styled(PerfectScrollbarComponent)({
  maxHeight: '80vh'
})

const ScrollWrapper = ({ children, hidden }: { children: ReactNode; hidden: boolean }) => {
  if (hidden) {
    return <Box sx={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden' }}>{children}</Box>
  } else {
    return <PerfectScrollbar options={{ wheelPropagation: false, suppressScrollX: true }}>{children}</PerfectScrollbar>
  }
}

const CoreNotificationDropdown = (props: Props) => {
  // ** Props
  const { settings } = props

  // ** States
  const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null)

  // ** Hook
  const theme = useTheme()
  const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))
  const navigate = useNavigate()

  // ** Vars
  const { direction } = settings

  const handleDropdownOpen = (event: SyntheticEvent) => {
    setAnchorEl(event.currentTarget)
  }

  const handleDropdownClose = () => {
    setAnchorEl(null)
  }

  const handleSeeAll = () => {
    navigate('/solutions/core/notifications')
    setAnchorEl(null)
  }

  // ** Service Hooks
  const user = useUser()
  const {
    data: notifications,
    subscribe,
    unsubscribe,
    isLoading
  } = useGetNotification({
    userId: user.data?.data?.id as string
  })
  const actionReadNotif = useReadNotifications()
  const actionReadAllNotif = useReadAllNotification()

  const unreadNotifications = notifications.filter(notif => !notif.is_read)

  // ** Mutation Handlers
  const handleReadSingle = async (notifId: string | number) => {
    try {
      await actionReadNotif.mutateAsync({ userId: user.data?.data?.id as string, notifIds: [notifId as string] })
      queryClient.invalidateQueries()
    } catch (error) {
      console.log(error)
    }
  }

  const handleReadAll = async () => {
    try {
      await actionReadAllNotif.mutateAsync({ userId: user.data?.data?.id as string })
      queryClient.invalidateQueries()
    } catch (error) {
      console.log(error)
    }
  }

  // ** Effects
  useEffect(() => {
    subscribe()

    return () => {
      unsubscribe()
    }
  }, [user.data])

  return (
    <>
      <IconButton color='inherit' aria-haspopup='true' onClick={handleDropdownOpen} aria-controls='customized-menu'>
        <Badge
          color='error'
          sx={{
            '& .MuiBadge-badge': { top: 4, right: 4, boxShadow: theme => `0 0 0 2px ${theme.palette.background.paper}` }
          }}
          badgeContent={unreadNotifications.length}
        >
          <Icon fontSize='1.5rem' icon='tabler:bell' />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDropdownClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'ltr' ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: direction === 'ltr' ? 'right' : 'left' }}
        sx={{
          '& .MuiPaper-root': {
            border: `1px solid ${theme.colorToken.border.neutral.normal}` // Change border color here
          }
        }}
      >
        <ScrollWrapper hidden={hidden}>
          <Notification
            data={notifications}
            onClose={handleDropdownClose}
            handleReadSingle={handleReadSingle}
            handleReadAll={handleReadAll}
            handleSeeAll={handleSeeAll}
          />
        </ScrollWrapper>
      </Menu>
    </>
  )
}

const WMS_PREFIX = '/solutions/wms'

/** Lonceng topbar: di solusi WMS memakai Pusat Notifikasi GEN-03; solusi lain tetap memakai notifikasi inti. */
const NotificationDropdown = (props: Props) => {
  const { pathname } = useLocation()
  return pathname.startsWith(WMS_PREFIX) ? <WmsNotificationBell /> : <CoreNotificationDropdown {...props} />
}

export default NotificationDropdown
